import MarketingNewPaidForm from "@/components/marketingPages/MarketingNewPaidForm";
import { ExpandableTile } from "@/components/pages/infoTile";
import { TimerOnlyText } from "@/components/pages/timer/timer";
import { formatDate, formatTime } from "@/marketingUtils/dateTimeParser";
import { getToken } from "@/marketingUtils/utils";
import mixpanel from "@/utils/mixpanel";
import fbq from "@/utils/tracker";
import axios from "axios";
import dayjs from "dayjs";
import {
  Award,
  BookOpen,
  Briefcase,
  Eye,
  Gem,
  GraduationCap,
  Heart,
} from "lucide-react";
import { GetServerSidePropsContext } from "next";
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { AiOutlineEye } from "react-icons/ai";
import { FaChartLine } from "react-icons/fa";
import { GiDiceTwentyFacesTwenty } from "react-icons/gi";
import { PiNumberCircleFourFill } from "react-icons/pi";

const items = [
  {
    icon: <FaChartLine className="w-8 h-8" />,
    text: "Take your numerology practice from a hobby to a high-earning profession.",
  },
  {
    icon: <GiDiceTwentyFacesTwenty className="w-8 h-8" />,
    text: "Start earning up to ₹1 Lakh per month by providing expert numerology readings",
  },
  {
    icon: <AiOutlineEye className="w-8 h-8" />,
    text: "Become a certified numerologist with our globally recognized certification.",
  },
  {
    icon: <PiNumberCircleFourFill className="w-8 h-8" />,
    text: "Learn to do accurate predictions in just a 2-hour masterclass.",
  },
];

const faqs = [
  {
    q: "Is it a live or pre-recorded workshop?",
    a: "It is a live workshop with interactive Q&A and personal insights.",
  },
  {
    q: "Is this class suitable for beginners?",
    a: "Yes, this session is designed for both beginners and those looking to deepen their understanding.",
  },
  {
    q: "In what language will the class be conducted?",
    a: "The class will be in a mix of Hindi and English for easy understanding.",
  },
  {
    q: "Do I need prior knowledge of numerology?",
    a: "No prior experience is required—just come with an open mind and willingness to explore.",
  }
];

const testimonials = [
  {
    name: "Anuj Verma",
    description:
      "Dr. Chaitanyaa Missra's numerology workshop opened my eyes to the deeper meaning of my birth numbers. His insights were accurate, empowering, and truly life-changing.",
  },
  {
    name: "Rajat Sharma",
    description:
      "The energy mapping through numbers and personalized analysis were spot on. Dr. Chaitanyaa's ability to decode patterns helped me align better with my goals.",
  },
  {
    name: "Priyanshu Menon",
    description:
      "Learning numerology from Dr. Chaitanyaa Missra was a spiritual awakening. His approach combines ancient wisdom with clear, practical tools for transformation.",
  },
  {
    name: "Mayank Sharma",
    description:
      "I've attended many workshops, but Dr. Chaitanyaa Missra's numerology sessions are in a league of their own. The personal name analysis and yearly cycles were incredibly insightful.",
  },
];

const cards = [
  {
    icon: <Briefcase className="w-10 h-10 text-orange-600" />,
    title: "Take Smart Business Decisions",
    desc: "Learn to choose the right business aligned to your DOB and avoid making wrong moves. It's time to take smart decisions and excel at them.",
  },
  {
    icon: <GraduationCap className="w-10 h-10 text-orange-600" />,
    title: "Beat Your Failures & Win Your Career",
    desc: "Know about the secrets that millionaires like Ambani have applied to reach where they are right now. Numerology can help you become the king of your field.",
  },
  {
    icon: <Gem className="w-10 h-10 text-orange-600" />,
    title: "Find Your Soulmate & Save Your Relationship",
    desc: "Finding the true love of your life is not difficult if you follow numerological practices. Know about the compatible numbers and find your life partner.",
  },
  {
    icon: <Heart className="w-10 h-10 text-orange-600" />,
    title: "Lead A Happy Married Life",
    desc: "The ups and downs of your relationship can be taken care of with the help of numerology. It can save your relationship from divorce.",
  },
];

const stats = [
  {
    icon: GraduationCap,
    title: "20+ Years",
    subtitle: "Experience",
  },
  {
    icon: Eye,
    title: "100k+",
    subtitle: "Students Taught",
  },
  {
    icon: BookOpen,
    title: "Practical Wisdom",
    subtitle: "Blending Tradition & Modernity",
  },
  {
    icon: Award,
    title: "Trusted Mentor",
    subtitle: "Across India & Beyond",
  },
];

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { id } = context.params as { id: string };
  const slug = "chaitanyaa-numero";

  const host = context.req?.headers.host;
  const protocol = context.req?.headers["x-forwarded-proto"] || "http";
  const fullDomain = `${protocol}://${host}`;
  const abTestType = "1-field-lp-ab";

  try {
    const { data } = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_API}masterclasses/${id}?abtest=true&abtesttype=${abTestType}`,
      {
        headers: {
          "Accept-Encoding": "gzip",
        },
      }
    );

    return {
      props: {
        masterclassId: id,
        masterclass: data.data,
        slug,
      },
    };
  } catch (err) {
    return {
      props: {
        masterclassId: id,
        masterclass: {},
        slug,
      },
    };
  }
}

export default function LongFree({
  masterclass,
  slug,
  abTestCounter,
  abTestType,
}: {
  masterclass: any;
  slug: string;
  abTestCounter: number;
  abTestType?: string;
}) {
  const router = useRouter();
  const slots = masterclass?.slots;
  const activeSlot = slots?.find((slot: any) => slot.active);
  const activeDate = formatDate(activeSlot?.startDateTime);
  const activeTimeSlot = formatTime(activeSlot?.startDateTime);
  const [isLeadFormOpen, setIsLeadFormOpen] = useState(false);
  const is2FieldSlug = [
    "mahesh-lalkitab",
    "vaibhav-bnn",
    "krish-kp",
    "tarun-regression",
  ].includes(slug);
  const is1FieldLp: "A" | "B" = abTestCounter % 2 == 1 ? "B" : "A";
  const isCounter50: "A" | "B" = abTestCounter % 2 == 1 ? "B" : "A";

  const handleLeadFormOpen = (segment: string) => {
    setIsLeadFormOpen(true);
    //@ts-ignore
    if (fbq) {
      //@ts-ignore
      fbq("trackSingleCustom", `Register Now Button Clicked`);
    }
    mixpanel.track("enroll_now", {
      landing_page: "chaitanyaa-numero",
      masterclass: masterclass?.title,
      masterclassId: masterclass?.id,
      platform: "astrolearn",
      segment: "chaitanyaa-numero" + segment,
    });
  };
  const mobileFormRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    mixpanel.track("marketing_page", {
      page_location: window.location.href,
      page_referrer: document.referrer,
      masterclass: masterclass?.title,
      landing_page: "chaitanyaa-numero",
      masterclassId: masterclass?.id,
      platform: "astrolearn",
      activeSlot: activeSlot?.id,
      activeWaGroup: activeSlot?.whatsappGroupLink,
      slug: slug,
      startDateTime: activeSlot?.startDateTime,
    });
    mixpanel.track("marketing_ab_test", {
      platform: "astrolearn",
      masterclassId: masterclass?.id,
      landing_page: "chaitanyaa-numero",
      abtesttype: abTestType,
      bucket: `${is1FieldLp}`,
      activeSlot: activeSlot?.id,
      activeWaGroup: activeSlot?.whatsappGroupLink,
      slug: slug,
      startDateTime: activeSlot?.startDateTime,
    });
  }, []);
  const [price, setPrice] = useState<PriceObj>({
    price: masterclass.price,
    discountedPrice: "0",
    workshopTitle: masterclass.title,
    source: "free_masterclass",
  });
  const source = router.query.source;
  const isTaboola = source === "Taboola";

  const fulldomain = process.env.NEXT_PUBLIC_FULLDOMAIN;

  const tkn = router.query.tkn;
  const bkt = router.query.bkt;
  const tokenObj = getToken((tkn as string) || undefined);

  const uiPrice = tokenObj?.discountedPrice ?? price?.discountedPrice;
  const ctaText =
    tkn && bkt == "B"
      ? "Register Your Seat Now"
      : `Register Now For ${uiPrice === "0" ? "FREE" : "₹" + uiPrice}`;
  const formCtaText =
    tkn && bkt == "B"
      ? "Register  Your Seat Now"
      : `Reserve Your Seat For Just ₹${uiPrice}`;

  return (
    <>
      <main className="w-full pb-24">
        {/* Phone View Form */}
        <div className="max-sm:fixed sm:hidden bottom-0 left-0 z-[60] right-0 w-full border-dashed">
          <MarketingNewPaidForm
            price={price}
            masterclass={masterclass}
            isFreeMc={!tkn}
            type="normal"
            showEmailField={false}
            showNameField={false}
            bucket={is1FieldLp}
            abTestType={abTestType}
            slug={slug}
            customCtaText="Register Now "
            btnClass="!bg-red-500 animate-pulse"
            formHeading=""
            redirectUrl={`/${masterclass?.id}/redirect/thank-you`}
            mrktToken={tokenObj?.token}
          />
        </div>

        {/* Desktop View Form */}
        <div className="max-md:hidden">
          <MarketingNewPaidForm
            isOpen={isLeadFormOpen}
            closeModal={() => setIsLeadFormOpen(false)}
            price={price}
            masterclass={masterclass}
            isFreeMc={!tkn}
            type="modal"
            showEmailField={false}
            showNameField={false}
            bucket={is1FieldLp}
            abTestType={abTestType}
            slug={slug}
            customCtaText="Register Now "
            btnClass="!bg-red-500"
            redirectUrl={`/${masterclass?.id}/redirect/thank-you`}
            mrktToken={tokenObj?.token}
          />
        </div>

        {/* Hero section */}
        <section className="relative bg-white px-4 pt-10 pb-10 max-sm:pt-5 max-sm:pb-8">
          <div className="max-w-6xl mx-auto flex flex-col items-center text-center">
            {/* TOP PILLOW TAG */}
            {/* <div className="bg-red-500/30 border border-red-500 text-red-500 px-6 py-2 rounded-full text-sm font-medium mb-6 animate-pulse max-sm:px-10">
              Tired of Facing the Same Problems Again and Again?
            </div> */}

            {/* MAIN HEADING */}
            <h1 className="text-4xl max-sm:text-2xl font-extrabold">
              Individuals Are Now{" "}
              <span className="text-red-500">Discovering The Hidden</span>
              <br />
              Stories Behind Their Repeating Struggles With
              <br />
              <span className="text-red-500 border-b-4 border-red-500">
                Numerology
              </span>
            </h1>

            {/* SUBTITLE */}
            <p className="mt-6 text-gray-500 max-w-3xl text-sm sm:text-base md:text-lg leading-relaxed max-sm:text-sm">
              The Proven Numerology Method That Gives Clients The Root-Cause
              Clarity Meditation, Tarot, And Therapy Never Could — So They
              Finally Heal For Good.
            </p>
          </div>

          {/* MAIN GRID */}
          <div className="max-w-6xl mx-auto mt-10 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
            {/* LEFT IMAGE SECTION */}
            <div className="flex justify-center">
              <img
                src="/chaitanyaa-numerology/teacher-banner.webp"
                alt="Masterclass Banner"
                className="rounded-lg shadow-lg w-full max-w-xl"
              />
            </div>

            {/* RIGHT INFO CARDS */}
            <div className="flex flex-col gap-5">
              {/* ROW 1 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 text-left py-5 px-5 max-sm:py-3 rounded-lg border border-black/10 shadow-sm">
                  <p className="text-red-500 font-semibold">SLOT DATE</p>
                  <p className="text-lg font-bold mt-1">{activeDate}</p>
                </div>

                <div className="bg-gray-50 text-left p-5 rounded-lg border border-black/10 shadow-sm max-sm:py-3">
                  <p className="text-red-500 font-semibold">SLOT TIME</p>
                  <p className="text-lg font-bold mt-1">{activeTimeSlot}</p>
                </div>
              </div>

              {/* ROW 2 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 text-left p-5 rounded-lg border border-black/10 shadow-sm max-sm:py-2">
                  <p className="text-red-500 font-semibold">LANGUAGE</p>
                  <p className="text-lg font-bold mt-1">Hinglish</p>
                </div>

                <div className="bg-gray-50 text-left p-5 rounded-lg border border-black/10 shadow-sm max-sm:py-2">
                  <p className="text-red-500 font-semibold">DURATION</p>
                  <p className="text-lg font-bold mt-1">90 Minutes</p>
                </div>
              </div>

              <div className="w-full flex flex-col text-center">
                <button
                  className="hidden sm:flex relative bg-red-500 !text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold sm:text-lg shadow-md hover:bg-red-600 transition-all duration-200 w-full sm:w-auto"
                  onClick={() => handleLeadFormOpen("what_will_you_learn")}
                >
                  <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-xl" />
                  Register Now
                </button>
                <p className="text-red-600 mt-2 font-medium max-md:hidden">
                  100% Beginner Friendly Live Session
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ---------Why Join This Webinar---------- */}
        <section className="bg-gray-100 flex flex-col justify-center items-center px-4 sm:px-8 md:px-24 pt-5">
          <div className="text-center py-10 px-4 relative">
            <h2 className="text-red-500 text-3xl md:text-4xl font-bold mb-10">
              Why Join This Webinar?
            </h2>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mt-20 max-sm:mt-10">
              {cards.map((card, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg border border-red-500/30 shadow-sm py-6 px-6 max-sm:py-5 max-sm:px-5 flex flex-col items-center transition-transform duration-300"
                >
                  <div className="mb-4 max-sm:mb-2">{card.icon}</div>
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    {card.title}
                  </h3>
                  <p className="text-gray-600 text-sm">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA button */}
          <div className="w-full mt-12 flex justify-center">
            <button
              className="hidden sm:flex relative bg-red-500 !text-white py-3 px-6 sm:px-8 sm:py-4 rounded-xl font-semibold sm:text-lg shadow-md hover:bg-red-600 transition-all duration-200 w-[500px]"
              onClick={() => handleLeadFormOpen("what_will_you_learn")}
            >
              <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-xl" />
              Register Now
            </button>
          </div>
          <p className="text-red-600 mt-2 font-medium max-md:hidden">
            100% Beginner Friendly Live Session
          </p>
        </section>

        {/* You should join if you are */}
        <section className="bg-gray-100 text-center md:py-24">
          <h2 className="text-2xl md:text-3xl font-bold text-red-500 md:mb-10">
            Why You Should Join this
          </h2>
          <div className="max-w-6xl mx-auto flex flex-col gap-4 max-sm:items-center px-7 mt-10">
            {items.map((item, i) => (
              <div
                key={i}
                className="flex sm:items-center gap-3 max-sm:gap-1 max-sm:items-center border border-red-500/30 md:px-8 max-sm:px-3 py-2 rounded-lg bg-gray-50"
              >
                <span className="">{item.icon}</span>
                <span className="font-medium text-gray-800 text-lg max-sm:text-sm">
                  {item.text}
                </span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 flex justify-center w-full">
            <button
              className="hidden sm:flex relative bg-red-500 !text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold sm:text-lg shadow-md hover:shadow-lg hover:bg-red-600 transition-all duration-200 w-[500px]"
              onClick={() => handleLeadFormOpen("why_this_mc")}
            >
              <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-xl" />
              Register Now
            </button>
          </div>
          <p className="text-red-600 mt-2 font-medium max-md:hidden">
            100% Beginner Friendly Live Session
          </p>
        </section>

        {/* Teacher Section */}
        <section className="relative pt-12 pb-12 max-sm:pb-5 px-6 overflow-hidden">
          <div className="flex flex-col items-center relative z-10 max-w-6xl mx-auto">
            {/* Heading */}
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-500 text-center">
              Why Learn From
              <br />
              <span className="text-red-500 border-b-4 border-red-500">
                {" "}
                Dr. Chaitanyaa Missra
              </span>
            </h2>

            <div className="flex flex-wrap justify-center">
              {/* Profile Card */}
              <div className="flex flex-col">
                <div className="w-80 h-80 relative overflow-hidden">
                  <Image
                    src="/chaitanyaa-numerology/teacher.webp"
                    alt="Chaitanyaa Missra"
                    fill
                    className="object-cover"
                  />
                </div>

                <div className="bg-gradient-to-r from-red-400 to-red-500 px-6 py-2.5 rounded-lg font-semibold text-white !border-none">
                  Numerologist & Spiritual Mentor
                </div>
              </div>

              {/* Description */}
              <div className="flex flex-col max-sm:text-center gap-3 mt-5 max-sm:mt-5 items-center">
                <p className="text-gray-700 max-w-3xl mx-auto leading-relaxed md:px-28">
                  Dr. Chaitanyaa Missra is a seasoned Numerologist and Spiritual
                  Mentor with 20+ years of experience helping thousands of
                  students decode their life path through the mystical science
                  of numbers.
                  <br />
                  His work empowers individuals to align with their soul's
                  vibration and manifest abundance, clarity, and purpose.
                  <br /> <br />
                  <span className="italic text-gray-500 text-center">
                    His students say, “Chaitanyaa's insights are accurate,
                    empowering, and truly life-changing.”
                  </span>
                </p>
              </div>
            </div>

            {/* Stats Section */}
            <div className="grid md:grid-cols-4 gap-6 mt-12 max-sm:px-5">
              {stats.map((stat, idx) => {
                const Icon = stat.icon; // dynamic component
                return (
                  <div
                    key={idx}
                    className="bg-gray-50 rounded-md border border-red-500/30 shadow-sm py-6 px-5 flex flex-col items-center text-center"
                  >
                    <Icon size={32} className="text-orange-600 mb-3" />
                    <h3 className="font-bold text-lg text-red-600">
                      {stat.title}
                    </h3>
                    <p className="text-black">{stat.subtitle}</p>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <div className="mt-12 flex justify-center w-full">
              <button
                className="hidden sm:flex relative bg-red-600 !text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold sm:text-lg shadow-md hover:shadow-lg hover:bg-red-600 transition-all duration-200 w-[500px] !border-white/60"
                onClick={() => handleLeadFormOpen("why_this_mc")}
              >
                <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-lg" />
                Register Now
              </button>
            </div>
            <p className="text-red-600 mt-2 font-medium max-md:hidden">
              100% Beginner Friendly Live Session
            </p>
          </div>
        </section>

        {/* Unable to Decide + Testimonials */}
        <section className="bg-gray-100 w-full flex flex-col items-center py-10 max-sm:pt-12 max-sm:pb-4 px-4 max-sm:px-7">
          {/* Heading */}
          <div className="text-center mb-10 max-w-3xl">
            <h2 className="text-xl md:text-2xl font-semibold text-red-500">
              Unable to Decide?
            </h2>
            <h1 className="text-2xl md:text-3xl font-bold mt-1">
              Here Are Real Feedbacks From Students
            </h1>
          </div>

          <div className="w-full">
            <div className="flex flex-nowrap max-sm:flex-col max-sm:items-center sm:flex-wrap justify-center gap-3 max-w-7xl mx-auto py-4 max-sm: ">
              {testimonials?.map((t, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl shadow-sm border border-gray-300 px-6 py-4 min-w-[260px] w-[260px] flex-shrink-0 flex flex-col max-sm:w-[300px]"
                >
                  {/* User Image */}
                  <div className="flex gap-2 items-center mb-4">
                    <img
                      src={`/long-page/testimonials/person${i + 1}.png`}
                      alt={`student-${i + 1}`}
                      className="w-16 h-16 rounded-full shadow-sm"
                    />
                    <div className="">
                      <p className="font-semibold text-gray-900 text-md">
                        {t?.name}
                      </p>
                      <div className="flex items-center gap-1 text-green-600">
                        <span className="text-green-600 text-lg">★★★★★</span>
                        <span className="font-semibold text-sm">Verified</span>
                      </div>
                    </div>
                  </div>

                  {/* Review */}
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {t?.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 flex justify-center w-full">
            <button
              className="hidden sm:flex relative bg-red-600 !text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold sm:text-lg shadow-md hover:shadow-lg hover:bg-red-600 transition-all duration-200 w-[500px] !border-white/60"
              onClick={() => handleLeadFormOpen("why_this_mc")}
            >
              <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-lg" />
              Register Now
            </button>
          </div>
          <p className="text-red-600 mt-2 font-medium max-md:hidden">
            100% Beginner Friendly Live Session
          </p>
        </section>

        {/* FAQ Section */}
        <div className="my-8 sm:my-12 md:my-10 px-4">
          <div className="flex flex-col items-center justify-center gap-3 px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-red-500">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="w-full flex flex-col gap-5 items-center max-w-3xl mx-auto md:mt-20 max-sm:mt-7 mb-10 max-sm:mb-40">
            {faqs.map((faq, index) => (
              <ExpandableTile className="" header={faq.q} key={index}>
                {faq.a}
              </ExpandableTile>
            ))}
          </div>
        </div>

        {/* CTA */}
        {bkt != "B" ? (
          <div
            className={`fixed bottom-0 z-30 lg:px-[110px] p-[20px] w-full flex justify-between items-center bg-white shadow-[0_35px_60px_-15px_rgba(0,0,0,0.6)]`}
          >
            <div>
              <div className="animate-pulse">
                <b className="text-black text-3xl">
                  {uiPrice === "0" ? "Free" : "₹" + uiPrice}
                </b>{" "}
                <span className=" line-through text-xs text-[#7B7D7C]">
                  ₹999
                </span>
              </div>
              {!isTaboola ? (
                <div className="text-xs text-[#7B7D7C]">
                  Offer Expires in{" "}
                  <b className="text-black">
                    <TimerOnlyText
                      tillDateTime={dayjs().add(15, "minute").toDate()}
                    />
                  </b>
                </div>
              ) : null}
            </div>
            <div>
              <button
                onClick={() => handleLeadFormOpen("cta")}
                className="bg-red-500 md:py-3 !text-white md:text-lg rounded-lg  max-md:hidden flex-row  gap-1 items-center justify-center hover:scale-105 relative !border-none animate-pulse"
              >
                <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-md" />
                {ctaText}
              </button>
            </div>
          </div>
        ) : (
          <div className="fixed bottom-0 z-30 p-0 w-full flex justify-center px-4 py-2 items-center bg-white shadow-[0_35px_60px_-15px_rgba(0,0,0,0.6)]">
            <button
              onClick={() => handleLeadFormOpen("footer")}
              className="bg-purple-500 md:py-3 py-4 max-md:text-lg !text-white md:text-lg rounded-lg  max-md:hidden flex flex-row  gap-1 items-center justify-center hover:scale-105 relative md:max-w-md !border-none"
            >
              <div className="absolute inset-0 bg-[linear-gradient(110deg,#00000000,45%,#ffffff80,55%,#00000000)] bg-[length:200%_100%] animate-shimmer rounded-md" />
              {ctaText}
            </button>
          </div>
        )}
      </main>
    </>
  );
}
